import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  getTeams, getTeamStats, createTeam, updateTeam, deleteTeam,
  addTeamMember, removeTeamMember, updateTeamMember,
} from '../../api/teams';
import { getUsers } from '../../api/users';
import { unwrapList, extractErrorMessage } from '../../utils/api';
import Dropdown from '../../components/Dropdown/Dropdown';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import { roleConfig, TEAM_TYPE_TO_ROLE } from '../../config/rolePermissions';
import { useI18n } from '../../i18n/I18nContext';
import './Teams.css';

// Readable, reasonably strong temp password for a new staff login.
function genPassword() {
  const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ', L = 'abcdefghijkmnpqrstuvwxyz', N = '23456789', S = '!@#$%';
  const r = (set) => set[Math.floor(Math.random() * set.length)];
  const body = Array.from({ length: 6 }, () => r(L + N)).join('');
  return r(U) + body + r(N) + r(S);
}

const S = (p) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
const ICON = {
  call: S(<><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></>),
  sales: S(<><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>),
  control: S(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>),
  general: S(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></>),
  users: S(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  layers: S(<><path d="M12 2l10 6-10 6L2 8z" /><path d="M2 14l10 6 10-6" /></>),
  plus: S(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>),
  edit: S(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" /></>),
  trash: S(<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></>),
  star: S(<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />),
  x: S(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
};

const TEAM_TYPES = {
  CALL_CENTER: { icon: 'call', accent: 'indigo' },
  SALES: { icon: 'sales', accent: 'green' },
  CONTROL: { icon: 'control', accent: 'amber' },
  GENERAL: { icon: 'general', accent: 'slate' },
};
const TEAM_TYPE_LABEL_KEY = { CALL_CENTER: 'typeCallCenter', SALES: 'typeSales', CONTROL: 'typeControl', GENERAL: 'typeGeneral' };
const TEAM_TYPE_BLURB_KEY = { CALL_CENTER: 'typeCallCenterBlurb', SALES: 'typeSalesBlurb', CONTROL: 'typeControlBlurb', GENERAL: 'typeGeneralBlurb' };
const TYPE_ORDER = ['CALL_CENTER', 'SALES', 'CONTROL', 'GENERAL'];

// Token-driven (not literal hex) so avatar tints stay legible in dark mode too.
const AVATAR_COLORS = [
  ['var(--color-secondary-soft)', 'var(--color-secondary-accent)'],
  ['var(--color-primary-soft)', 'var(--color-primary-accent)'],
  ['var(--color-teal-soft)', 'var(--color-teal)'],
  ['var(--color-success-soft)', 'var(--color-success)'],
  ['var(--color-coral-soft)', 'var(--color-coral)'],
  ['var(--color-info-soft)', 'var(--color-info)'],
  ['var(--color-danger-soft)', 'var(--color-danger)'],
  ['var(--color-surface-muted)', 'var(--color-text-secondary)'],
];
const initials = (n) => {
  const p = String(n || '?').trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?';
};
const av = (n) => { let h = 0; for (const c of String(n || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0; const [background, color] = AVATAR_COLORS[h % AVATAR_COLORS.length]; return { background, color }; };

const EMPTY_TEAM = { name: '', type: 'CALL_CENTER', description: '', isActive: true };
// i18n key of an account role's name, for telling the admin what an existing account is.
const ACCOUNT_ROLE_KEY = {
  ADMIN: 'team.roleAdmin',
  TEACHER: 'team.roleTeacher',
  STUDENT: 'team.roleStudent',
  MODERATOR: 'teams.people.roleModerator',
  MENTOR: 'teams.people.roleMentor',
};

function Teams({ embedded = false }) {
  const { t } = useI18n();
  const typeLabel = (tp) => (TEAM_TYPE_LABEL_KEY[tp] ? t(`teams.${TEAM_TYPE_LABEL_KEY[tp]}`) : '');
  const typeBlurb = (tp) => (TEAM_TYPE_BLURB_KEY[tp] ? t(`teams.${TEAM_TYPE_BLURB_KEY[tp]}`) : '');
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamModal, setTeamModal] = useState(null); // { mode, form, id }
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [membersFor, setMembersFor] = useState(null); // team object
  const [addUserId, setAddUserId] = useState('');
  const [inviteContact, setInviteContact] = useState(''); // email or phone
  const [inviting, setInviting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', contact: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null); // { name, contact, password } shown once

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [teamsRes, s] = await Promise.all([getTeams(), getTeamStats().catch(() => null)]);
      setTeams(unwrapList(teamsRes));
      setStats(s);
    } catch {
      toast.error(t('teams.toastLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Backend caps `limit` at 100 (422 above that) — was silently failing the
    // whole request at 200, which left the "add existing student" list empty.
    getUsers({ limit: 100 }).then((r) => setUsers(unwrapList(r))).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    TYPE_ORDER.forEach((tp) => { g[tp] = []; });
    teams.forEach((t) => { (g[t.type] || (g[t.type] = [])).push(t); });
    return g;
  }, [teams]);

  const openCreate = () => { setFormErr(null); setTeamModal({ mode: 'create', form: { ...EMPTY_TEAM } }); };
  const openEdit = (t) => { setFormErr(null); setTeamModal({ mode: 'edit', id: t.id, form: { name: t.name, type: t.type, description: t.description || '', isActive: t.isActive ?? true } }); };
  const setField = (k, v) => setTeamModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  const saveTeam = async (e) => {
    e.preventDefault();
    const f = teamModal.form;
    if (!f.name.trim() || f.name.trim().length < 2) return setFormErr(t('teams.toastTeamNameRequired'));
    setSaving(true); setFormErr(null);
    const payload = { name: f.name.trim(), type: f.type, description: f.description.trim() || undefined, isActive: f.isActive };
    try {
      if (teamModal.mode === 'edit') { await updateTeam(teamModal.id, payload); toast.success(t('teams.toastTeamUpdated')); }
      else { await createTeam(payload); toast.success(t('teams.toastTeamCreated')); }
      setTeamModal(null); await load();
    } catch (err) {
      setFormErr(extractErrorMessage(err, t('teams.toastSaveTeamFailed')));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteTeam(confirmDel.id);
      // Staff logins left in no team are switched off by the backend; say how many.
      const off = res?.deactivatedUserIds?.length || 0;
      if (off) toast(t('teams.toastTeamDeletedLoginsOff', { count: off }), { duration: 7000 });
      else toast.success(t('teams.toastTeamDeleted'));
      setConfirmDel(null); await load();
    }
    catch { toast.error(t('teams.toastDeleteFailed')); }
  };

  // Member management
  const memberIds = new Set((membersFor?.members || []).map((m) => m.userId));
  const assignableUsers = users.filter((u) => !memberIds.has(u.id));

  const refreshTeam = (updated) => {
    setMembersFor(updated);
    setTeams((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  const handleAddMember = async () => {
    if (!addUserId) return;
    try {
      const updated = await addTeamMember(membersFor.id, { userId: addUserId });
      refreshTeam(updated); setAddUserId(''); loadStatsQuiet();
    } catch (err) { toast.error(extractErrorMessage(err, t('teams.toastAddMemberFailed'))); }
  };

  // Add by email or phone: enrolls the matching student, or stores a pending invite.
  const handleInvite = async () => {
    const v = inviteContact.trim();
    if (!v) return;
    const body = v.includes('@') ? { email: v } : { phone: v };
    setInviting(true);
    try {
      const updated = await addTeamMember(membersFor.id, body);
      const match = (updated.members || []).find((m) => m.email === v || m.phone === v);
      refreshTeam(updated); setInviteContact(''); loadStatsQuiet();
      toast.success(match && match.status === 'INVITED' ? t('teams.toastInvited') : t('teams.toastMemberAdded'));
    } catch (err) {
      toast.error(extractErrorMessage(err, t('teams.toastAddMemberFailed')));
    } finally { setInviting(false); }
  };
  // Create a real staff login so the member can sign in immediately.
  const openCreate2 = () => {
    setCreateForm({ name: '', contact: '', password: genPassword() });
    setCreateOpen(true);
  };
  const handleCreateStaff = async () => {
    const name = createForm.name.trim();
    const contact = createForm.contact.trim();
    const password = createForm.password;
    if (!name) return toast.error(t('teams.toastEnterName'));
    if (!contact) return toast.error(t('teams.toastEnterContact'));
    if (!password || password.length < 6) return toast.error(t('teams.toastPasswordMinLength'));
    const body = { name, password, ...(contact.includes('@') ? { email: contact } : { phone: contact }) };
    setCreating(true);
    try {
      // The team comes back with what the call did: `createdAccount` is false when the
      // contact already had an account, which is only linked (its password and role stay).
      const { createdAccount, existingRole, ...updated } = await addTeamMember(membersFor.id, body);
      refreshTeam(updated); loadStatsQuiet();
      setCreateOpen(false);
      setCreateForm({ name: '', contact: '', password: '' });
      if (createdAccount === false) {
        const role = ACCOUNT_ROLE_KEY[existingRole] ? t(ACCOUNT_ROLE_KEY[existingRole]) : existingRole || '';
        toast(t('teams.toastLinkedExisting', { role }), { duration: 7000 });
      } else {
        setCreatedCreds({ name, contact, password });
        toast.success(t('teams.toastStaffLoginCreated'));
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, t('teams.toastCreateStaffFailed')));
    } finally { setCreating(false); }
  };

  const handleRemoveMember = async (memberId) => {
    const member = (membersFor?.members || []).find((m) => m.id === memberId);
    try {
      const { deactivatedUserIds, ...updated } = await removeTeamMember(membersFor.id, memberId);
      refreshTeam(updated); loadStatsQuiet();
      // A MODERATOR login in no team any more is switched off by the backend.
      if (member?.userId && deactivatedUserIds?.includes(member.userId)) {
        const name = member.user?.name || member.email || member.phone || '';
        toast(t('teams.toastMemberLoginOff', { name }), { duration: 7000 });
      }
    }
    catch { toast.error(t('teams.toastRemoveMemberFailed')); }
  };
  const handleToggleLead = async (m) => {
    try { const updated = await updateTeamMember(membersFor.id, m.id, { isLead: !m.isLead }); refreshTeam(updated); }
    catch { toast.error(t('teams.toastUpdateMemberFailed')); }
  };
  const loadStatsQuiet = () => { getTeamStats().then(setStats).catch(() => {}); };

  return (
    <div className="tm-page">
      <div className="tm-head" style={embedded ? { justifyContent: 'flex-end' } : undefined}>
        {!embedded && (
          <div className="tm-head-text">
            <span className="tm-eyebrow">{t('teams.eyebrow')}</span>
            <h1 className="page-title">{t('teams.title')}</h1>
            <p className="page-sub">{t('teams.subtitle')}</p>
          </div>
        )}
        <button type="button" className="ds-btn ds-btn-primary" onClick={openCreate}>{ICON.plus} {t('teams.newTeam')}</button>
      </div>

      <div className="tm-stats">
        <div className="tm-stat is-indigo"><span className="tm-stat-icon">{ICON.layers}</span><span className="tm-stat-value">{stats?.total ?? teams.length}</span><span className="tm-stat-label">{t('teams.statTeams')}</span></div>
        <div className="tm-stat is-green"><span className="tm-stat-icon">{ICON.users}</span><span className="tm-stat-value">{stats?.totalMembers ?? teams.reduce((sum, tm) => sum + (tm._count?.members || tm.members?.length || 0), 0)}</span><span className="tm-stat-label">{t('teams.statMembers')}</span></div>
        <div className="tm-stat is-amber"><span className="tm-stat-icon">{ICON.call}</span><span className="tm-stat-value">{stats?.byType?.CALL_CENTER ?? (grouped.CALL_CENTER?.length || 0)}</span><span className="tm-stat-label">{t('teams.statCallCenter')}</span></div>
        <div className="tm-stat is-slate"><span className="tm-stat-icon">{ICON.sales}</span><span className="tm-stat-value">{stats?.byType?.SALES ?? (grouped.SALES?.length || 0)}</span><span className="tm-stat-label">{t('teams.statSales')}</span></div>
      </div>

      {loading ? (
        <LoadingState message={t('teams.loading')} />
      ) : teams.length === 0 ? (
        <PageEmptyState variant="students" title={t('teams.emptyTitle')}
          description={t('teams.emptyDesc')}
          actionLabel={t('teams.emptyAction')} onAction={openCreate} />
      ) : (
        TYPE_ORDER.filter((tp) => (grouped[tp] || []).length).map((tp) => (
          <section key={tp} className="tm-section">
            <div className="tm-section-head">
              <span className={`tm-section-icon acc-${TEAM_TYPES[tp].accent}`}>{ICON[TEAM_TYPES[tp].icon]}</span>
              <h2 className="tm-section-title">{typeLabel(tp)}</h2>
              <span className="tm-section-sub">{typeBlurb(tp)}</span>
            </div>
            <div className="tm-grid">
              {grouped[tp].map((team) => {
                const members = team.members || [];
                const lead = members.find((m) => m.isLead);
                return (
                  <div key={team.id} className={`tm-card ${team.isActive ? '' : 'is-inactive'}`}>
                    <div className="tm-card-top">
                      <span className={`tm-card-icon acc-${TEAM_TYPES[team.type].accent}`}>{ICON[TEAM_TYPES[team.type].icon]}</span>
                      <div className="tm-card-actions">
                        <button type="button" className="tm-icon-btn" title={t('teams.edit')} aria-label={t('teams.edit')} onClick={() => openEdit(team)}>{ICON.edit}</button>
                        <button type="button" className="tm-icon-btn is-danger" title={t('teams.delete')} aria-label={t('teams.delete')} onClick={() => setConfirmDel(team)}>{ICON.trash}</button>
                      </div>
                    </div>
                    <h3 className="tm-card-name">{team.name}{!team.isActive && <span className="tm-inactive-pill">{t('teams.inactive')}</span>}</h3>
                    {team.description && <p className="tm-card-desc">{team.description}</p>}
                    <div className="tm-card-members">
                      <div className="tm-avatars">
                        {members.slice(0, 5).map((m) => {
                          const label = m.user?.name || m.email || m.phone;
                          return <span key={m.id} className={`tm-avatar ${m.status === 'INVITED' ? 'is-invited' : ''}`} style={av(label)} title={`${label}${m.status === 'INVITED' ? ` · ${t('teams.invited').toLowerCase()}` : ''}`}>{initials(m.user?.name || m.email)}</span>;
                        })}
                        {members.length > 5 && <span className="tm-avatar tm-avatar-more">+{members.length - 5}</span>}
                        {members.length === 0 && <span className="tm-no-members">{t('teams.noMembersYet')}</span>}
                      </div>
                      <span className="tm-member-count">{members.length === 1 ? t('teams.memberCountOne') : t('teams.memberCount', { count: members.length })}</span>
                    </div>
                    {lead && <div className="tm-lead">{ICON.star} {t('teams.leadPrefix')} <strong>{lead.user?.name || lead.email || lead.phone}</strong></div>}
                    <button type="button" className="tm-manage-btn" onClick={() => { setMembersFor(team); setAddUserId(''); setCreateOpen(false); }}>{t('teams.manageMembers')}</button>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {/* Create / edit team modal */}
      {teamModal && (
        <div className="tm-modal-overlay" onClick={() => setTeamModal(null)}>
          <form className="tm-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveTeam}>
            <div className="tm-modal-head"><h3>{teamModal.mode === 'edit' ? t('teams.editTeamTitle') : t('teams.newTeamTitle')}</h3><button type="button" className="tm-modal-close" onClick={() => setTeamModal(null)} aria-label={t('common.close')}>{ICON.x}</button></div>
            <div className="tm-modal-body">
              <div className="tm-field"><label>{t('teams.teamNameLabel')}</label><input type="text" value={teamModal.form.name} onChange={(e) => setField('name', e.target.value)} placeholder={t('teams.teamNamePlaceholder')} maxLength={80} /></div>
              <div className="tm-field">
                <label>{t('teams.teamTypeLabel')}</label>
                <Dropdown value={teamModal.form.type} onChange={(v) => setField('type', v)}
                  options={TYPE_ORDER.map((tp) => ({ value: tp, label: typeLabel(tp) }))} />
                <span className="tm-help">{typeBlurb(teamModal.form.type)}</span>
              </div>
              <div className="tm-field"><label>{t('teams.descriptionLabel')} <span className="tm-optional">{t('teams.optional')}</span></label><textarea rows={2} value={teamModal.form.description} onChange={(e) => setField('description', e.target.value)} placeholder={t('teams.descriptionPlaceholder')} maxLength={300} /></div>
              <label className="tm-active-row"><input type="checkbox" checked={teamModal.form.isActive} onChange={(e) => setField('isActive', e.target.checked)} /><span>{t('teams.activeTeam')}</span></label>
              {formErr && <p className="tm-form-err">{formErr}</p>}
            </div>
            <div className="tm-modal-actions">
              <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setTeamModal(null)}>{t('teams.cancel')}</button>
              <button type="submit" className="ds-btn ds-btn-primary" disabled={saving}>{saving ? t('teams.saving') : teamModal.mode === 'edit' ? t('teams.saveChanges') : t('teams.createTeam')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Members drawer */}
      {membersFor && (
        <div className="tm-modal-overlay" onClick={() => setMembersFor(null)}>
          <div className="tm-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="tm-modal-head">
              <div>
                <h3>{membersFor.name}</h3>
                {(() => {
                  const dm = membersFor.members || [];
                  const enrolled = dm.filter((m) => m.status !== 'INVITED').length;
                  const invited = dm.length - enrolled;
                  return (
                    <span className="tm-drawer-sub">
                      {typeLabel(membersFor.type)} · {t('teams.drawerEnrolled', { count: enrolled })}{invited ? ` · ${t('teams.drawerInvitedSuffix', { count: invited })}` : ''}
                    </span>
                  );
                })()}
              </div>
              <button type="button" className="tm-modal-close" onClick={() => setMembersFor(null)} aria-label={t('common.close')}>{ICON.x}</button>
            </div>
            <div className="tm-drawer-add">
              <Dropdown value={addUserId} onChange={setAddUserId} placeholder={t('teams.selectStudentPlaceholder')}
                options={assignableUsers.map((u) => ({ value: u.id, label: `${u.name || u.email} · ${u.role}` }))} className="tm-add-select" ariaLabel={t('teams.addExistingStudentAria')} />
              <button type="button" className="ds-btn ds-btn-primary" onClick={handleAddMember} disabled={!addUserId}>{ICON.plus} {t('teams.add')}</button>
            </div>
            <div className="tm-drawer-or"><span>{t('teams.orAddByContact')}</span></div>
            <div className="tm-drawer-add">
              <input className="tm-invite-input" type="text" value={inviteContact} aria-label={t('teams.addByContactAria')}
                onChange={(e) => setInviteContact(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInvite(); } }}
                placeholder={t('teams.contactPlaceholder')} />
              <button type="button" className="ds-btn ds-btn-primary" onClick={handleInvite} disabled={!inviteContact.trim() || inviting}>{ICON.plus} {inviting ? t('teams.adding') : t('teams.add')}</button>
            </div>

            {/* Create a real staff login so the new member can actually sign in. */}
            {!createOpen ? (
              <button type="button" className="tm-create-toggle" onClick={openCreate2}>{ICON.plus} {t('teams.createStaffLoginToggle')}</button>
            ) : (
              <div className="tm-create-panel">
                {(() => {
                  const rc = roleConfig(TEAM_TYPE_TO_ROLE[membersFor.type] || 'STAFF');
                  return (
                    <div className="tm-perm-preview">
                      <span className="tm-perm-role" style={{ background: rc.accentSoft, color: rc.accent }}>{t('teams.signsInAs', { role: t(rc.labelKey) })}</span>
                      <div className="tm-perm-cols">
                        <div><span className="tm-perm-h tm-perm-can">{t('teams.canAccess')}</span><ul>{rc.canKeys.map((k) => <li key={k}>{t(k)}</li>)}</ul></div>
                        {rc.cannotKeys.length > 0 && <div><span className="tm-perm-h tm-perm-cannot">{t('teams.cannotAccess')}</span><ul>{rc.cannotKeys.map((k) => <li key={k}>{t(k)}</li>)}</ul></div>}
                      </div>
                    </div>
                  );
                })()}
                <div className="tm-create-field"><label>{t('teams.fullNameLabel')}</label><input type="text" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('teams.fullNamePlaceholder')} /></div>
                <div className="tm-create-field"><label>{t('teams.contactLabel')}</label><input type="text" value={createForm.contact} onChange={(e) => setCreateForm((f) => ({ ...f, contact: e.target.value }))} placeholder={t('teams.contactPlaceholder2')} /></div>
                <div className="tm-create-field"><label>{t('teams.tempPasswordLabel')}</label>
                  <div className="tm-pw-row">
                    <input type="text" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} />
                    <button type="button" className="tm-pw-btn" title={t('teams.generate')} aria-label={t('teams.generate')} onClick={() => setCreateForm((f) => ({ ...f, password: genPassword() }))}>↻</button>
                    <button type="button" className="tm-pw-btn" title={t('teams.copy')} aria-label={t('teams.copy')} onClick={() => { navigator.clipboard?.writeText(createForm.password); toast.success(t('teams.copied')); }}>⧉</button>
                  </div>
                  <span className="tm-create-hint">{t('teams.shareHint')}</span>
                </div>
                <div className="tm-create-actions">
                  <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setCreateOpen(false)}>{t('teams.cancel')}</button>
                  <button type="button" className="ds-btn ds-btn-primary" onClick={handleCreateStaff} disabled={creating}>{creating ? t('teams.creating') : t('teams.createLogin')}</button>
                </div>
              </div>
            )}

            <div className="tm-member-list">
              {(membersFor.members || []).length === 0 ? (
                <p className="tm-no-members-msg">{t('teams.noMembersYetLong')}</p>
              ) : (
                (membersFor.members || []).map((m) => {
                  const invited = m.status === 'INVITED';
                  const label = m.user?.name || m.email || m.phone || '—';
                  return (
                  <div key={m.id} className="tm-member-row">
                    <span className={`tm-avatar ${invited ? 'is-invited' : ''}`} style={av(label)}>{initials(m.user?.name || m.email)}</span>
                    <div className="tm-member-info">
                      <span className="tm-member-name">{label}{m.isLead && <span className="tm-lead-badge">{t('teams.lead')}</span>}{invited && <span className="tm-invited-badge">{t('teams.invited')}</span>}</span>
                      <span className="tm-member-sub">{m.user ? `${m.user.email} · ${m.user.role}` : (m.phone || m.email || t('teams.pendingSignup'))}</span>
                    </div>
                    <button type="button" className={`tm-lead-btn ${m.isLead ? 'is-on' : ''}`} title={m.isLead ? t('teams.removeLead') : t('teams.makeLead')} aria-label={m.isLead ? t('teams.removeLead') : t('teams.makeLead')} onClick={() => handleToggleLead(m)}>{ICON.star}</button>
                    <button type="button" className="tm-icon-btn is-danger" title={t('teams.remove')} aria-label={t('teams.remove')} onClick={() => handleRemoveMember(m.id)}>{ICON.x}</button>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* One-time credentials display after creating a staff login. */}
      {createdCreds && (
        <div className="tm-modal-overlay" onClick={() => setCreatedCreds(null)}>
          <div className="tm-modal tm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="tm-modal-head"><h3>{t('teams.staffLoginCreatedTitle')}</h3><button type="button" className="tm-modal-close" onClick={() => setCreatedCreds(null)} aria-label={t('common.close')}>{ICON.x}</button></div>
            <div className="tm-modal-body">
              <p className="tm-creds-note">{t('teams.shareCreds', { name: createdCreds.name })}</p>
              <div className="tm-creds-box">
                <div className="tm-creds-row"><span>{t('teams.loginLabel')}</span><code>{createdCreds.contact}</code></div>
                <div className="tm-creds-row"><span>{t('teams.passwordLabel')}</span><code>{createdCreds.password}</code></div>
              </div>
              <button type="button" className="ds-btn ds-btn-secondary tm-creds-copy" onClick={() => { navigator.clipboard?.writeText(`${t('teams.loginLabel')}: ${createdCreds.contact}\n${t('teams.passwordLabel')}: ${createdCreds.password}`); toast.success(t('teams.copied')); }}>{t('teams.copyLoginPassword')}</button>
            </div>
            <div className="tm-modal-actions">
              <button type="button" className="ds-btn ds-btn-primary" onClick={() => setCreatedCreds(null)}>{t('teams.done')}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="tm-modal-overlay" onClick={() => setConfirmDel(null)}>
          <div className="tm-modal tm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="tm-modal-head"><h3>{t('teams.deleteTeamTitle')}</h3></div>
            <div className="tm-modal-body"><p>{t('teams.deleteTeamMsg', { name: confirmDel.name })}</p></div>
            <div className="tm-modal-actions">
              <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setConfirmDel(null)}>{t('teams.cancel')}</button>
              <button type="button" className="ds-btn ds-btn-danger" onClick={handleDelete}>{t('teams.deleteAction')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Teams;
